/**
 * NFC 스캔 — 태그 UID 를 읽는다.
 *
 *   iOS 네이티브     : WebView 브릿지(BIZNOMI_BRIDGE) → CoreNFC
 *   Android 네이티브 : WebView 브릿지 → NfcAdapter
 *   Android Chrome   : Web NFC API (NDEFReader) — serialNumber 만 읽힌다
 *   그 외            : 미지원
 *
 * 정품 서명(nfcSignature, NXP NTAG21x)은 네이티브에서만 읽을 수 있다.
 * 즉 순수 웹 태깅은 UID 만 있는 약한 증거다 — 기기 신원이 'browser' 인 것과 같은 결.
 */

import { isNativeApp } from './device'

export interface NfcScanResult {
  /** 태그 하드웨어 UID (예: 04:62:C3:5A:08:17:91) */
  nfcUid: string
  /** NXP 정품 서명 32바이트 hex — 네이티브에서만 */
  nfcSignature?: string
}

interface BridgeResponse {
  action: string
  requestId: string
  success: boolean
  payload?: Record<string, unknown>
}

declare global {
  interface Window {
    BIZNOMI_BRIDGE_CALLBACK?: (data: BridgeResponse) => void
    NDEFReader?: new () => {
      scan(opts: { signal: AbortSignal }): Promise<void>
      addEventListener(
        type: string,
        cb: (e: { serialNumber?: string }) => void,
        opts?: { signal: AbortSignal },
      ): void
    }
  }
}

const SCAN_TIMEOUT_MS = 30_000

/** 사용자가 스캔 시트를 닫은 경우 — 오류가 아니라 취소다 */
export class NfcCancelled extends Error {
  constructor() {
    super('스캔을 취소했습니다')
    this.name = 'NfcCancelled'
  }
}

/**
 * 사용자에게 그대로 보여도 되는 메시지.
 *
 * ★ 플랫폼이 준 문자열("NotSupportedError", "Session invalidated…", 네이티브 원문)은
 *   절대 화면에 띄우지 않는다. 직원은 그걸 읽고도 뭘 해야 할지 모르고, 앱이 고장난
 *   것처럼 느낀다. 실제로 그 토스트가 운영에서 문제가 됐다 (버그로그 [7]).
 *   원문은 console 로만 남기고, 화면에는 우리가 쓴 문장만 나간다.
 */
export class NfcUserError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NfcUserError'
  }
}

/** 플랫폼 오류를 감싼다 — 원문은 로그로, 화면에는 우리 문장으로 */
function wrapPlatformError(err: unknown, fallback: string): NfcUserError {
  console.error('[NFC]', err)
  return new NfcUserError(fallback)
}

export function isNfcSupported(): boolean {
  if (typeof window === 'undefined') return false
  return isNativeApp() || 'NDEFReader' in window
}

/** UID 정규화 — 대문자 + 콜론 구분 (04:62:C3:5A:08:17:91) */
export function formatUid(raw: string): string {
  const hex = raw.toUpperCase().replace(/[^A-F0-9]/g, '')
  return hex.replace(/(.{2})(?=.)/g, '$1:')
}

function postToBridge(message: string): boolean {
  const ios = window.webkit?.messageHandlers?.BIZNOMI_BRIDGE
  if (ios) {
    ios.postMessage(message)
    return true
  }
  if (window.BIZNOMI_BRIDGE) {
    window.BIZNOMI_BRIDGE.postMessage(message)
    return true
  }
  return false
}

function scanViaBridge(): Promise<NfcScanResult> {
  return new Promise((resolve, reject) => {
    const requestId = `nfc_${Date.now()}`

    const timer = setTimeout(() => {
      cleanup()
      reject(new NfcUserError('NFC 스캔 시간이 초과되었습니다'))
    }, SCAN_TIMEOUT_MS)

    function cleanup() {
      clearTimeout(timer)
      window.BIZNOMI_BRIDGE_CALLBACK = undefined
    }

    window.BIZNOMI_BRIDGE_CALLBACK = (data) => {
      if (data.requestId !== requestId || data.action !== 'OPEN_NFC_SCAN') return
      cleanup()

      if (!data.success) {
        const raw = String(data.payload?.error ?? '')
        // iOS "Session invalidated by user" / Android "cancelled" 등
        if (/cancel|취소|invalidated by user/i.test(raw)) {
          reject(new NfcCancelled())
          return
        }
        // 네이티브 원문은 로그로만. 화면에는 우리 문장이 나간다
        reject(wrapPlatformError(raw, 'NFC 태그를 읽지 못했습니다. 다시 대주세요.'))
        return
      }

      const uid = String(data.payload?.nfcUid ?? data.payload?.ndef ?? '')
      if (!uid) {
        reject(new NfcUserError('NFC 태그를 읽을 수 없습니다. 태그에 폰을 다시 대주세요.'))
        return
      }
      const signature = data.payload?.nfcSignature
      resolve({
        nfcUid: formatUid(uid),
        ...(typeof signature === 'string' ? { nfcSignature: signature.toUpperCase() } : {}),
      })
    }

    if (!postToBridge(JSON.stringify({ action: 'OPEN_NFC_SCAN', requestId, payload: {} }))) {
      cleanup()
      reject(new NfcUserError('앱과 연결되지 않았습니다. 앱을 다시 열어 주세요.'))
    }
  })
}

function scanViaWebNfc(): Promise<NfcScanResult> {
  return new Promise((resolve, reject) => {
    const abort = new AbortController()
    const timer = setTimeout(() => {
      abort.abort()
      reject(new NfcUserError('NFC 스캔 시간이 초과되었습니다'))
    }, SCAN_TIMEOUT_MS)

    const done = () => {
      clearTimeout(timer)
      abort.abort()
    }

    try {
      const Reader = window.NDEFReader
      if (!Reader) throw new NfcUserError('NFC를 지원하지 않는 기기입니다')
      const ndef = new Reader()

      ndef
        .scan({ signal: abort.signal })
        .then(() => {
          ndef.addEventListener(
            'reading',
            ({ serialNumber }) => {
              done()
              if (serialNumber) resolve({ nfcUid: formatUid(serialNumber) })
              else reject(new NfcUserError('NFC 태그를 읽을 수 없습니다. 태그에 폰을 다시 대주세요.'))
            },
            { signal: abort.signal },
          )
          ndef.addEventListener(
            'readingerror',
            () => {
              done()
              reject(new NfcUserError('NFC 읽기에 실패했습니다. 다시 시도해 주세요.'))
            },
            { signal: abort.signal },
          )
        })
        .catch((err: Error) => {
          clearTimeout(timer)
          if (err.name === 'AbortError') return
          // 사용자가 권한을 거부한 경우도 취소로 다룬다
          reject(
            err.name === 'NotAllowedError'
              ? new NfcCancelled()
              : wrapPlatformError(err, 'NFC를 시작하지 못했습니다. 잠시 후 다시 시도해 주세요.'),
          )
        })
    } catch (err) {
      clearTimeout(timer)
      reject(
        err instanceof NfcUserError
          ? err
          : wrapPlatformError(err, 'NFC를 시작하지 못했습니다. 잠시 후 다시 시도해 주세요.'),
      )
    }
  })
}

/** NFC 스캔 시작 — 태그 UID 반환 */
export function startNfcScan(): Promise<NfcScanResult> {
  if (typeof window === 'undefined') return Promise.reject(new NfcUserError('NFC를 사용할 수 없습니다'))
  if (isNativeApp()) return scanViaBridge()
  if ('NDEFReader' in window) return scanViaWebNfc()
  return Promise.reject(new NfcUserError('NFC를 지원하지 않는 기기입니다'))
}
